import { useCallback, useEffect, useRef, useState } from 'react';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { trackCartItemCount, trackCartPreviewRebuild } from '@jsmdg/tracking';
import { isSSR } from '@jsmdg/yoshi';
import { type CartPreview } from '../../shared/types';

let cartPromise: Promise<CartPreview> | null = null;

function useCart(): CartPreview | null {
    const [cart, setCart] = useState<CartPreview | null>(null);
    const { fetchCart }: { fetchCart: () => Promise<CartPreview> } = useFragmentContext();
    const hasFetched = useRef(false);

    const fetch = useCallback(async (): Promise<void> => {
        if (hasFetched.current) {
            return;
        }

        /*
         * if users have cookie disabled, it doesn't make sense to fetch the cart which is session related
         */
        if (!cartPromise && navigator.cookieEnabled) {
            hasFetched.current = true;
            cartPromise = fetchCart();

            try {
                const fetchedCart = await cartPromise;
                if (
                    fetchedCart.itemsCount > 0 &&
                    fetchedCart.rebuilt &&
                    fetchedCart.trackingLabel
                ) {
                    trackCartPreviewRebuild(fetchedCart.trackingLabel);
                }

                trackCartItemCount(fetchedCart.itemsCount);
                setCart(fetchedCart);
            } catch (error) {
                logToSentry(error as Error);
                setCart(null);
            }
        } else if (cartPromise) {
            try {
                setCart(await cartPromise);
            } catch (error) {
                logToSentry(error as Error);
                setCart(null);
            }
        }
    }, [fetchCart]);

    useEffect(() => {
        if (isSSR) {
            return;
        }

        fetch();
    }, [fetch]);

    return cart;
}

export { useCart };
